import { useState, type ReactNode } from 'react';
import { CalendarClock, Check, CircleDot, X } from 'lucide-react';
import cronstrue from 'cronstrue';
import type { AgentDetail } from '@command-room/shared';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';
import type { ActivityItem, AgentProfile } from '@/lib/agentProfile';

const DONT_PREVIEW = 5;
const NOTES_PREVIEW = 3;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function ExpandableList({
  items,
  preview,
  icon,
  label,
}: {
  items: string[];
  preview: number;
  icon: ReactNode;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, preview);
  return (
    <>
      <ul className="space-y-2 text-sm">
        {shown.map((item) => (
          <li key={item} className="flex gap-2.5">
            <span className="mt-0.5 shrink-0">{icon}</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
      {items.length > preview && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-3 text-xs font-medium text-primary hover:underline"
        >
          {open ? 'Show less' : `Show all ${items.length} ${label}`}
        </button>
      )}
    </>
  );
}

function cronLabel(expr: string | null): string | null {
  if (!expr) return null;
  try {
    return cronstrue.toString(expr, { use24HourTimeFormat: true });
  } catch {
    return expr;
  }
}

const TONE_DOT: Record<ActivityItem['tone'], string> = {
  ok: 'bg-success',
  failed: 'bg-destructive',
  info: 'bg-primary',
};

function ActivityFeed({ items }: { items: ActivityItem[] }) {
  return (
    <ol className="space-y-3">
      {items.map((item) => (
        <li key={item.id} className="flex gap-3 text-sm">
          <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', TONE_DOT[item.tone])} aria-hidden="true" />
          <div className="min-w-0">
            <p>{item.text}</p>
            <p className="text-xs text-muted-foreground">{relativeTime(item.at)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function PersonalityBars({ bars }: { bars: AgentProfile['personality'] }) {
  return (
    <dl className="mt-5 space-y-2">
      {bars.map((bar) => (
        <div key={bar.label} className="grid grid-cols-[5.5rem_1fr_2.5rem] items-center gap-3 text-xs">
          <dt className="text-muted-foreground">{bar.label}</dt>
          <dd className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
            <div className="h-full rounded-full bg-primary/70" style={{ width: `${bar.pct}%` }} />
          </dd>
          <dd className="text-right tabular-nums">{bar.pct}%</dd>
        </div>
      ))}
    </dl>
  );
}

export function ProfileTab({ data, profile }: { data: AgentDetail; profile: AgentProfile }) {
  const { working, nextTask } = profile;
  const skillNames = data.skills.map((s) => s.skill_name);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="flex flex-col gap-6">
        {(profile.about || profile.personality.length > 0) && (
          <Section title="About">
            {profile.about && <p className="max-w-prose text-sm leading-relaxed">{profile.about}</p>}
            {profile.personality.length > 0 && <PersonalityBars bars={profile.personality} />}
          </Section>
        )}

        {profile.whatIDo.length > 0 && (
          <Section title="What I do">
            <ul className="space-y-2 text-sm">
              {profile.whatIDo.map((item) => (
                <li key={item} className="flex gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {profile.whatIDontDo.length > 0 && (
          <Section title="What I never do">
            <ExpandableList
              items={profile.whatIDontDo}
              preview={DONT_PREVIEW}
              label="limits"
              icon={<X className="h-4 w-4 text-destructive" aria-hidden="true" />}
            />
          </Section>
        )}

        <Section title="Activity">
          {profile.activity.length > 0 ? (
            <ActivityFeed items={profile.activity} />
          ) : (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          )}
        </Section>
      </div>

      <aside className="flex flex-col gap-6">
        <Section title="Right now">
          {working ? (
            <div className="space-y-1">
              <p className="flex items-center gap-2 text-sm font-medium">
                <CircleDot
                  className={cn('h-4 w-4', working.live ? 'animate-pulse text-warning' : 'text-muted-foreground')}
                  aria-hidden="true"
                />
                {working.live ? 'Working on' : 'Last worked on'}
              </p>
              <p className="text-sm">{working.text}</p>
              <p className="text-xs text-muted-foreground">{relativeTime(working.at)}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nothing yet.</p>
          )}
          {nextTask && (
            <div className="mt-4 border-t border-border pt-3">
              <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" /> Next up
              </p>
              <p className="mt-1 text-sm">{nextTask.label}</p>
              <p className="text-xs text-muted-foreground">
                {relativeTime(nextTask.next_run)}
                {cronLabel(nextTask.cron_expr) ? ` · ${cronLabel(nextTask.cron_expr)}` : ''}
              </p>
            </div>
          )}
        </Section>

        {(skillNames.length > 0 || profile.tools.length > 0) && (
          <Section title="Skills & tools">
            <div className="flex flex-wrap gap-1.5">
              {skillNames.map((name) => (
                <Badge key={`skill-${name}`} variant="secondary">
                  {name}
                </Badge>
              ))}
              {profile.tools.map((name) => (
                <Badge key={`tool-${name}`} variant="outline" className="font-mono text-[11px]">
                  {name}
                </Badge>
              ))}
            </div>
          </Section>
        )}

        {profile.goodToKnow.length > 0 && (
          <Section title="Good to know">
            <ExpandableList
              items={profile.goodToKnow}
              preview={NOTES_PREVIEW}
              label="notes"
              icon={<span className="block h-1.5 w-1.5 translate-y-1 rounded-full bg-muted-foreground" />}
            />
          </Section>
        )}

        <Section title="Details">
          <dl className="space-y-2 text-sm">
            {data.birthday && <Detail label="Birthday" value={data.birthday} />}
            <Detail label="Department" value={data.department ?? '—'} />
            <Detail label="Last seen" value={relativeTime(data.last_seen_at)} />
          </dl>
        </Section>
      </aside>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
